import { useIsOrgReadOnly } from '@/components/auth/claimsUtils';
import { useDeletePaymentMethodMutation, useEditPaymentMethodInformationMutation } from '@/services/paymentMethods';
import { Box, Button, Icon, Modal, RadioGroup, TextInput, toast } from '@palmetto/palmetto-components';
import { FormikValues, useFormik } from 'formik';
import { SyntheticEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { BankAccountTypes, SavedPaymentMethodFullResponse } from 'types';

export const AccountPaymentMethod = ({
  isModalOpen,
  closeModal,
  paymentMethods = [],
  accountHolderName = '',
}: {
  isModalOpen: boolean;
  closeModal: (event?: SyntheticEvent) => void;
  paymentMethods?: SavedPaymentMethodFullResponse[];
  accountHolderName?: string;
}) => {
  const { id = '' } = useParams<{ id: string }>();
  const [editPaymentMethodInformation] = useEditPaymentMethodInformationMutation();
  const [deletePaymentMethod] = useDeletePaymentMethodMutation();
  const [showAccountNumber, setShowAccountNumber] = useState(false);
  const allowsAction = useIsOrgReadOnly();
  const autoPayMethod = paymentMethods.find((m) => m.isAutoPayMethod);
  const formik = useFormik({
    initialValues: {
      bankAccountType: autoPayMethod?.bankAccount?.bankAccountType || BankAccountTypes.checking,
      routingNumber: autoPayMethod?.bankAccount?.routingNumber || '',
      accountNumber: autoPayMethod?.bankAccount?.accountNumber || '',
      isAccountValidated: true,
      confirmAccountNumber: '',
    },
    validationSchema: yup.object({
      routingNumber: yup
        .string()
        .required('The Routing Number is required')
        .matches(/^\d+$/, 'The Routing Number must contain only numbers')
        .matches(/^\d{9}$/, 'A valid U.S. routing number is 9 digits')
        .test('firstDigit', 'The first digit must be 0, 1, 2, or 3', (val) => /^[0123]/.test(val || '')),
      accountNumber: yup
        .string()
        .required('The Account Number is required')
        .matches(/^\d+$/, 'The Account Number must contain only numbers'),
      confirmAccountNumber: yup
        .string()
        .oneOf([yup.ref('accountNumber')], 'The Account Numbers must match')
        .required('The Account Number needs to be confirmed'),
    }),
    enableReinitialize: true,
    onSubmit: async (values: FormikValues, { setSubmitting, resetForm }) => {
      try {
        const previousAutoPayId = autoPayMethod?.id;
        await editPaymentMethodInformation({
          id,
          data: {
            bankAccount: {
              accountNumber: values.accountNumber,
              routingNumber: values.routingNumber,
              bankAccountType: values.bankAccountType,
              accountHolderName,
              isAccountValidated: values.isAccountValidated,
            },
            setAsAutoPay: true,
          },
        }).unwrap();
        if (previousAutoPayId) {
          try {
            await deletePaymentMethod({ accountId: id, financialAccountId: previousAutoPayId }).unwrap();
          } catch (e) {
            console.error('Failed to remove previous payment method after update:', e);
          }
        }
        toast.success('Payment Method updated successfully');
        setShowAccountNumber(false);
        resetForm();
        closeModal();
      } catch (e) {
        console.error(e);
        toast.error('Error editing Payment Method');
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Modal ariaLabelledBy="accountPaymentMethod" isOpen={isModalOpen} onDismiss={closeModal} maxWidth="500px">
      <Modal.Header id="accountPaymentMethod" title="Account Payment Method" onDismiss={closeModal} />
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body background="grey-50">
          <Box direction="column" childGap="xl">
            <RadioGroup
              title="Account type"
              id="bankAccountType"
              name="bankAccountType"
              label="Account Type"
              value={formik.values.bankAccountType}
              onChange={(event) => formik.setFieldValue('bankAccountType', event.target.value)}
              options={[
                { label: 'Checking', value: BankAccountTypes.checking, id: BankAccountTypes.checking },
                { label: 'Savings', value: BankAccountTypes.savings, id: BankAccountTypes.savings },
              ]}
            />
            <Box>
              <TextInput
                label="Routing Number"
                name="routingNumber"
                id="routingNumber"
                type="text"
                value={formik.values.routingNumber}
                onChange={(event) => formik.setFieldValue('routingNumber', event.target.value)}
                error={formik.touched.routingNumber && (formik.errors.routingNumber as string)}
              />
            </Box>
            <Box>
              <Box fontSize="sm" fontWeight="medium" padding="0 0 sm 0">
                Account Number
              </Box>
              <Box direction={{ base: 'column', tablet: 'row' }} gap="sm">
                <TextInput
                  label=""
                  hideLabel
                  name="accountNumber"
                  id="accountNumber"
                  type={showAccountNumber ? 'text' : 'password'}
                  value={formik.values.accountNumber}
                  onChange={(event) => formik.setFieldValue('accountNumber', event.target.value)}
                  error={formik.touched.accountNumber && (formik.errors.accountNumber as string)}
                />
                <Button
                  variant="secondary"
                  tone="neutral"
                  size="sm"
                  onClick={() => setShowAccountNumber(!showAccountNumber)}
                >
                  {showAccountNumber ? <Icon size="sm" name="eye" /> : <Icon size="sm" name="eye-slash" />}
                </Button>
              </Box>
            </Box>
            <Box>
              <TextInput
                label="Confirm Account Number"
                name="confirmAccountNumber"
                id="confirmAccountNumber"
                type={showAccountNumber ? 'text' : 'password'}
                onChange={(event) => formik.setFieldValue('confirmAccountNumber', event.target.value)}
                value={formik.values.confirmAccountNumber}
                error={formik.touched.confirmAccountNumber && (formik.errors.confirmAccountNumber as string)}
                onPaste={(event: any) => event.preventDefault()}
              />
            </Box>
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button
            as="button"
            onClick={() => {
              formik.resetForm();
              closeModal();
            }}
            variant="secondary"
            tone="neutral"
            size="md"
            isLoading={formik.isSubmitting}
          >
            Cancel
          </Button>
          <Button variant="primary" size="md" type="submit" isLoading={formik.isSubmitting} disabled={!allowsAction}>
            Save
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
